import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Loader2, Plus, Users, Eye, Copy, MessageSquare,
  CheckCircle, XCircle, Wifi, WifiOff, Image, MousePointer2,
  UserPlus, StopCircle, Heart, ThumbsUp, ThumbsDown, Send
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace('http', 'ws') || 'ws://localhost:8000';

export default function ProofingSessionPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [galleries, setGalleries] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentPhotoId, setCommentPhotoId] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [cursors, setCursors] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ gallery_id: '', client_name: '', client_email: '' });
  const ws = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchGalleries();
    return () => { if (ws.current) ws.current.close(); };
  }, []);

  const fetchGalleries = async () => {
    try {
      const res = await axios.get(`${API}/galleries`);
      setGalleries(res.data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const createSession = async () => {
    if (!createForm.gallery_id) {
      toast.error(language === 'pl' ? 'Wybierz galerie' : 'Select a gallery');
      return;
    }
    try {
      const res = await axios.post(`${API}/galleries/${createForm.gallery_id}/proofing-sessions`, {
        gallery_id: createForm.gallery_id,
        client_name: createForm.client_name,
        client_email: createForm.client_email,
      });
      toast.success(language === 'pl' ? 'Sesja utworzona!' : 'Session created!');
      setShowCreate(false);
      joinSession(res.data.session_id, createForm.gallery_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const joinSession = async (sessionId, galleryId) => {
    try {
      const sessionRes = await axios.get(`${API}/proofing-sessions/${sessionId}`);
      setActiveSession(sessionRes.data);

      const gid = galleryId || sessionRes.data.gallery_id;
      const photosRes = await axios.get(`${API}/galleries/${gid}/photos`);
      setPhotos(photosRes.data || []);

      // Load existing selections
      const existingSelections = new Set(
        (sessionRes.data.selections || []).map(s => s.photo_id)
      );
      setSelectedPhotos(existingSelections);
      setComments(sessionRes.data.comments || []);

      connectWebSocket(sessionId);
    } catch (e) {
      toast.error('Error joining session');
    }
  };

  const connectWebSocket = useCallback((sessionId) => {
    if (ws.current) ws.current.close();

    const wsUrl = `${WS_BASE}/api/ws/proofing/${sessionId}?user_id=${user?.id || 'photographer'}`;
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      setWsConnected(true);
    };

    socket.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);

        if (msg.type === 'user_joined') {
          setOnlineUsers(prev => [...new Set([...prev, msg.user_id])]);
        } else if (msg.type === 'user_left') {
          setOnlineUsers(prev => prev.filter(u => u !== msg.user_id));
          setCursors(prev => {
            const next = { ...prev };
            delete next[msg.user_id];
            return next;
          });
        } else if (msg.type === 'cursor') {
          setCursors(prev => ({
            ...prev,
            [msg.user_id]: { x: msg.x, y: msg.y, photo_id: msg.photo_id }
          }));
        } else if (msg.type === 'selection') {
          setSelectedPhotos(prev => {
            const next = new Set(prev);
            if (msg.selected) next.add(msg.photo_id);
            else next.delete(msg.photo_id);
            return next;
          });
        } else if (msg.type === 'comment') {
          setComments(prev => [...prev, msg]);
        } else if (msg.type === 'session_ended') {
          toast.info(language === 'pl' ? 'Sesja zakonczona' : 'Session ended');
          setActiveSession(null);
          setWsConnected(false);
        }
      } catch (err) { /* ignore parse errors */ }
    };

    socket.onclose = () => setWsConnected(false);
    socket.onerror = () => setWsConnected(false);
    ws.current = socket;
  }, [user, language]);

  const toggleSelection = (photoId) => {
    const isSelected = selectedPhotos.has(photoId);
    setSelectedPhotos(prev => {
      const next = new Set(prev);
      if (isSelected) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
    // Broadcast to other users
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'selection',
        photo_id: photoId,
        selected: !isSelected,
      }));
    }
  };

  const sendComment = () => {
    if (!newComment.trim()) return;
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'comment',
        photo_id: commentPhotoId,
        text: newComment.trim(),
      }));
    }
    setNewComment('');
  };

  const sendReaction = (photoId, emoji) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'reaction',
        photo_id: photoId,
        emoji: emoji,
      }));
    }
  };

  const endSession = async () => {
    if (!activeSession) return;
    try {
      await axios.post(`${API}/proofing-sessions/${activeSession.id}/end`);
      toast.success(language === 'pl' ? 'Sesja zakonczona' : 'Session ended');
      setActiveSession(null);
      if (ws.current) ws.current.close();
    } catch (e) {
      toast.error('Error ending session');
    }
  };

  const copyShareLink = () => {
    if (!activeSession) return;
    const link = `${window.location.origin}/proofing/${activeSession.id}?code=${activeSession.access_code}`;
    navigator.clipboard.writeText(link);
    toast.success(language === 'pl' ? 'Link skopiowany!' : 'Link copied!');
  };

  const handleMouseMove = useCallback((e) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) return;
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1);
    const y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1);
    ws.current.send(JSON.stringify({ type: 'cursor', x: parseFloat(x), y: parseFloat(y) }));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/proofing" />

      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-1 flex items-center gap-3">
              <Users className="w-8 h-8" />
              {language === 'pl' ? 'Live Proofing' : 'Live Proofing'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'pl'
                ? 'Przegladaj zdjecia z klientem w czasie rzeczywistym — wspolna selekcja i komentarze'
                : 'Review photos with client in real-time — shared selection and comments'}
            </p>
          </div>
          {!activeSession && (
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {language === 'pl' ? 'Nowa sesja' : 'New Session'}
            </Button>
          )}
        </div>

        {/* Create Session */}
        <AnimatePresence>
          {showCreate && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8"
            >
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'pl' ? 'Nowa sesja proofingu' : 'New Proofing Session'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'pl' ? 'Galeria' : 'Gallery'}</Label>
                      <select
                        value={createForm.gallery_id}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, gallery_id: e.target.value }))}
                        className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                      >
                        <option value="">{language === 'pl' ? '-- Wybierz --' : '-- Select --'}</option>
                        {galleries.map(g => (
                          <option key={g.id} value={g.id}>{g.name} ({g.photo_count} {language === 'pl' ? 'zdjec' : 'photos'})</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'pl' ? 'Imie klienta' : 'Client Name'}</Label>
                      <Input
                        value={createForm.client_name}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, client_name: e.target.value }))}
                        placeholder="Jan Kowalski"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        value={createForm.client_email}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, client_email: e.target.value }))}
                        placeholder="jan@example.com"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={createSession}>
                      <UserPlus className="w-4 h-4 mr-2" />
                      {language === 'pl' ? 'Utworz i dolacz' : 'Create & Join'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowCreate(false)}>
                      {language === 'pl' ? 'Anuluj' : 'Cancel'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Active Session */}
        {activeSession ? (
          <div className="space-y-6">
            {/* Session Bar */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-6">
                    <div>
                      <h2 className="font-bold">
                        {activeSession.client_name
                          ? `${language === 'pl' ? 'Sesja z' : 'Session with'} ${activeSession.client_name}`
                          : (language === 'pl' ? 'Sesja proofingu' : 'Proofing Session')}
                      </h2>
                      <p className="text-xs text-muted-foreground font-mono">
                        {language === 'pl' ? 'Kod' : 'Code'}: {activeSession.access_code}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {wsConnected ? (
                        <span className="flex items-center gap-1 text-green-500 text-sm">
                          <Wifi className="w-4 h-4" /> Live
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-muted-foreground text-sm">
                          <WifiOff className="w-4 h-4" /> Offline
                        </span>
                      )}
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">{language === 'pl' ? 'Wybrane' : 'Selected'}:</span>{' '}
                      <span className="font-bold text-green-500">{selectedPhotos.size}</span>
                      <span className="text-muted-foreground"> / {photos.length}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyShareLink}>
                      <Copy className="w-4 h-4 mr-1" />
                      {language === 'pl' ? 'Udostepnij' : 'Share'}
                    </Button>
                    <Button variant="destructive" size="sm" onClick={endSession}>
                      <StopCircle className="w-4 h-4 mr-1" />
                      {language === 'pl' ? 'Zakoncz' : 'End'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Photo Grid + Comments */}
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              {/* Photos */}
              <div
                className="xl:col-span-3 relative"
                ref={containerRef}
                onMouseMove={handleMouseMove}
              >
                {/* Remote cursors */}
                {Object.entries(cursors).map(([uid, pos]) => (
                  uid !== user?.id && (
                    <div
                      key={uid}
                      className="absolute pointer-events-none z-10 transition-all duration-100"
                      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                    >
                      <MousePointer2 className="w-5 h-5 text-blue-400 -rotate-12" />
                      <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded ml-1">
                        {uid.slice(0, 6)}
                      </span>
                    </div>
                  )
                ))}

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {photos.map((photo) => {
                    const isSelected = selectedPhotos.has(photo.id);
                    return (
                      <motion.div
                        key={photo.id}
                        whileHover={{ scale: 1.02 }}
                        className="relative group"
                      >
                        <div
                          className={`aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                            isSelected ? 'border-green-500 ring-2 ring-green-500/30' : 'border-transparent'
                          }`}
                          onClick={() => toggleSelection(photo.id)}
                        >
                          <img
                            src={photo.thumbnail_url || photo.url}
                            alt={photo.original_filename}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          {isSelected && (
                            <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
                              <CheckCircle className="w-5 h-5 text-white" />
                            </div>
                          )}
                        </div>

                        {/* Reaction buttons on hover */}
                        <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 justify-center">
                          {['❤️', '👍', '👎', '⭐'].map(emoji => (
                            <button
                              key={emoji}
                              onClick={(e) => { e.stopPropagation(); sendReaction(photo.id, emoji); }}
                              className="w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-sm transition-colors"
                            >
                              {emoji}
                            </button>
                          ))}
                          <button
                            onClick={(e) => { e.stopPropagation(); setCommentPhotoId(photo.id); }}
                            className="w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors"
                          >
                            <MessageSquare className="w-4 h-4 text-white" />
                          </button>
                        </div>

                        <p className="text-xs text-muted-foreground mt-1 truncate px-1">
                          {photo.original_filename}
                        </p>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Comments Panel */}
              <div className="xl:col-span-1">
                <Card className="sticky top-6">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      {language === 'pl' ? 'Komentarze' : 'Comments'}
                      {comments.length > 0 && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          {comments.length}
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Comment List */}
                    <div className="max-h-[400px] overflow-y-auto space-y-3">
                      {comments.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-4">
                          {language === 'pl' ? 'Brak komentarzy' : 'No comments yet'}
                        </p>
                      ) : (
                        comments.map((c, i) => (
                          <div key={i} className="text-sm bg-secondary/50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-xs">
                                {c.user_id === user?.id ? (language === 'pl' ? 'Ty' : 'You') : c.user_id?.slice(0, 8)}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {c.timestamp && new Date(c.timestamp).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p>{c.text}</p>
                            {c.photo_id && (
                              <span className="text-xs text-muted-foreground mt-1 block">
                                {language === 'pl' ? 'dot. zdjecia' : 're: photo'} #{photos.findIndex(p => p.id === c.photo_id) + 1}
                              </span>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                    {/* Comment Input */}
                    <div className="flex gap-2">
                      <Input
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder={language === 'pl' ? 'Napisz komentarz...' : 'Write a comment...'}
                        onKeyDown={(e) => e.key === 'Enter' && sendComment()}
                        className="text-sm"
                      />
                      <Button size="icon" onClick={sendComment} disabled={!newComment.trim()}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    {commentPhotoId && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{language === 'pl' ? 'Komentarz do zdjecia' : 'Comment on photo'} #{photos.findIndex(p => p.id === commentPhotoId) + 1}</span>
                        <button onClick={() => setCommentPhotoId(null)} className="text-destructive">
                          <XCircle className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        ) : (
          /* No Active Session */
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">
                {language === 'pl' ? 'Rozpocznij sesje proofingu' : 'Start a Proofing Session'}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {language === 'pl'
                  ? 'Utworz sesje, wyslij link klientowi i przegladajcie zdjecia razem w czasie rzeczywistym. Klient widzi Twoj kursor i moze dodawac komentarze.'
                  : 'Create a session, share the link with your client, and review photos together in real-time. Client sees your cursor and can add comments.'}
              </p>
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {language === 'pl' ? 'Nowa sesja' : 'New Session'}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
